import { createSignal } from 'solid-js'
import * as api from '../api/endpoints/credits.api'
import type { ActionCosts, CreditPack, CreditTransaction } from '../api/types/credits'
import type { I18nContextValue } from '../i18n'
import type { Toast } from './toast.store'

interface CreditsState {
  balance: number
  transactions: CreditTransaction[]
  packs: CreditPack[]
  stripeConfigured: boolean
  actionCosts: ActionCosts
  loaded: boolean
}

const [state, setState] = createSignal<CreditsState>({
  balance: 0,
  transactions: [],
  packs: [],
  stripeConfigured: false,
  actionCosts: {},
  loaded: false,
})

export const creditsStore = {
  getState: state,

  get state() {
    return state()
  },

  get balance() {
    return state().balance
  },

  get stripeConfigured() {
    return state().stripeConfigured
  },

  async loadAll() {
    try {
      const [balance, packs, costs] = await Promise.all([
        api.getBalance(),
        api.getPacks(),
        api.getActionCosts(),
      ])
      setState((s) => ({
        ...s,
        balance: balance.balance,
        transactions: balance.transactions,
        packs: packs.packs,
        stripeConfigured: packs.stripe_configured,
        actionCosts: costs,
        loaded: true,
      }))
    } catch (e) {
      console.error('Failed to load credits:', e)
    }
  },

  async refreshBalance() {
    try {
      const b = await api.getBalance()
      setState((s) => ({ ...s, balance: b.balance, transactions: b.transactions }))
    } catch {
      /* swallow */
    }
  },

  async createCheckout(slug: string) {
    return api.createCheckout(slug)
  },

  async requestMore() {
    return api.requestMore()
  },

  costFor(action: string) {
    return state().actionCosts[action] ?? 0
  },

  clear() {
    setState({
      balance: 0,
      transactions: [],
      packs: [],
      stripeConfigured: false,
      actionCosts: {},
      loaded: false,
    })
  },
}

export async function settleSpendAndNotify(
  beforeBalance: number,
  t: I18nContextValue['t'],
  addToast: (toast: Omit<Toast, 'id'>) => void
): Promise<void> {
  await creditsStore.refreshBalance()
  const newBalance = creditsStore.balance
  const spent = beforeBalance - newBalance
  if (spent > 0) {
    const unit =
      spent === 1 ? t('billing.confirmCost.creditSingular') : t('billing.confirmCost.creditPlural')
    addToast({
      type: 'success',
      message: t('billing.postSpend.spent', { count: spent, unit, balance: newBalance }),
    })
  }
}
